import React, { useMemo, useRef, useState } from 'react';
import { history } from 'umi';
import { Layout } from 'tea-component';
import CardLoading from '@op/components/CardLoading';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import { useFilterFromShortId } from '@opm/utils/history';
import useIntls from '@opm/hooks/useIntls';
import MarketContentBody from '@opm/components/MarketContentBody';
import ScrollBottomList from '@opm/components/ScrollBottomList';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { UgcCreativeTrendsDetailsPoint } from '@opm/operationLog/pages';
import { useUGCGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { useTopicTypeColorMap } from '@opm/pages/CreativeTrends/hooks';
import { useUgcTrendDetail } from '../hooks';
import { DETAIL_FILTER_KEYS } from '../constants';
import TrendInfo from './components/TrendInfo/index';
import RelatedVideosSection from './components/RelatedVideosSection/index';
import './index.scss';
import { VideoTranslateContext } from '@opm/pages/Videos/context';
import { LanguageType } from '@op/types/common';

const { Content } = Layout;

export default function UgcCreativeTrendsDetails() {
  DBOM_POINT.useInitPage({ buriedPoint: UgcCreativeTrendsDetailsPoint });
  const { intl, isEn } = useIntls();
  const { filter: userDefaultValue, loading: shortIdLoading } =
    useFilterFromShortId();
  const wholePageRef = useRef<HTMLDivElement>(null);

  // 翻译语言状态，通过 Context 下发给各 Tab
  const [language, setLanguage] = useState<LanguageType>('origin');


  // 标题 topic：后端按多语言下发 topic（zh）/ topic_en（en），按当前语言取值
  const headerTitle = useMemo(() => {
    const topic = isEn
      ? userDefaultValue?.topic_en || userDefaultValue?.topic
      : userDefaultValue?.topic;
    return topic || intl('Creative Trend Details');
  }, [isEn, userDefaultValue, intl]);
  const {
    filter: detailsFilter,
    setFilter: setUserFilter,
    loading: filterLoading,
    ...filterProps
  } = useFilterCommon({
    keys: [...DETAIL_FILTER_KEYS],
  });

  const filter = useMemo(() => {
    if (!detailsFilter) return null;
    return {
      ...userDefaultValue,
      ...detailsFilter,
      source_type: 'ugc',
    };
  }, [detailsFilter, userDefaultValue]);

  const { data: trendData, loading: trendLoading } = useUgcTrendDetail({
    filter,
  });
  const { gameMap } = useUGCGameMap();
  // Topic Type chip 配色映射（UGC 用 ugc_trends_config_topic_types）
  const { topicTypeColorMap } = useTopicTypeColorMap(
    'ugc_trends_config_topic_types',
  );

  return (
    <VideoTranslateContext.Provider value={{ language, setLanguage }}>
      <ScrollBottomList>
        <Content className="dbom-ugc-trends-details" ref={wholePageRef}>
          <CardLoading loading={shortIdLoading}>
            <Content.Header
              title={headerTitle}
              showBackButton
              onBackButtonClick={() => {
                history.goBack();
              }}
            />
            <MarketContentBody
              loading={filterLoading}
              filterSlot={
                <FilterCommon
                  {...filterProps}
                  userDefaultValue={userDefaultValue}
                  loadingHeight={150}
                  useOriDefaultValue
                  realTimeMode
                  confirm={(value: any) => {
                    setUserFilter(value);
                  }}
                />
              }
            >
              <TrendInfo
                data={trendData}
                loading={!!trendLoading}
                filter={filter}
                gameMap={gameMap}
                topicTypeColorMap={topicTypeColorMap}
              />
              <RelatedVideosSection filter={filter} />
            </MarketContentBody>
          </CardLoading>
        </Content>
      </ScrollBottomList>
    </VideoTranslateContext.Provider>
  );
}
